import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { detect } from "detect-browser";
import * as Sentry from "@sentry/react";
import ApplicantReview from "../../apiResources/ApplicantReview";
import FormStep from "./FormStep";
import InternetExplorerSupport from "../shared/general/InternetExplorerSupport";
import ReviewAlreadyLeft from "./ReviewAlreadyLeft";
import Loading from "./Loading";
import OopsPage from "./OopsPage";
import {
  Wrapper,
  FormWrapper,
  Title,
  Form,
} from "./styles/applicant-review-form-styles";

class ApplicantReviewForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      formLoading: false,
      candidateName: null,
      employerName: null,
      recruiterName: null,
      agencyName: null,
      jobTitle: null,
      termsAccepted: false,
      step: 0,
      reviewMessage: "",
      communicationRating: null,
      professionalismRating: null,
      supportRating: null,
      recommendRating: null,
      published: false,
      ieMessageActive: detect().name === "ie",
      error: null,
    };
  }

  async componentDidMount() {
    const isIe = detect().name === "ie";
    if (isIe) {
      document.body.classList.add("internet-explorer");
    }

    const { match } = this.props;
    await this.fetchReview(match.params.id);
  }

  fetchReview = async (reviewId) => {
    try {
      const { data } = await ApplicantReview.find(reviewId);

      this.setState({
        loading: false,
        error: null,
        agencyName: data.recruiterName,
        candidateName: data.candidateFirstName,
        employerName: data.employerName,
        recruiterName: data.recruiterUserFullName,
        jobTitle: data.jobTitle,
        published: data.published,
      });
    } catch (error) {
      Sentry.captureException(error);

      this.setState({
        loading: false,
        error,
      });
    }
  };

  // close IE support banner
  closeIeMessage = () => {
    this.setState({ ieMessageActive: false });
  };

  // accept terms checkbox
  checkBoxOnChange = (e) => {
    if (e.target.checked) {
      this.setState({ termsAccepted: true });
    } else {
      this.setState({ termsAccepted: false });
    }
  };

  // proceed to review steps if terms were accepted
  proceedToReview = () => this.setState({ step: 1 });

  // set review message state
  setReviewMessage = (e) => {
    this.setState({
      reviewMessage: e.target.value,
    });
  };

  // set star ratings state
  setRating = (e) => {
    const { step } = this.state;

    this.setState({
      [e.target.name]: parseInt(e.target.value, 10),
    });

    setTimeout(() => {
      this.setState({
        step: step + 1,
      });
    }, 1300);
  };

  // submit data
  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ formLoading: true });

    const { match } = this.props;
    const {
      communicationRating,
      professionalismRating,
      supportRating,
      recommendRating,
      reviewMessage,
    } = this.state;

    try {
      const review = await ApplicantReview.find(match.params.id);

      review.data.assignAttributes({
        communication: communicationRating,
        professionalism: professionalismRating,
        published: true,
        message: reviewMessage,
        recommend: recommendRating,
        support: supportRating,
      });

      await review.data.save();

      this.setState({
        formLoading: false,
        step: 6,
        error: null,
      });
    } catch (error) {
      Sentry.captureException(error);

      this.setState({ error });
    }
  };

  render() {
    const {
      loading,
      formLoading,
      termsAccepted,
      candidateName,
      agencyName,
      employerName,
      recruiterName,
      jobTitle,
      ieMessageActive,
      step,
      reviewMessage,
      communicationRating,
      professionalismRating,
      supportRating,
      recommendRating,
      published,
      error,
    } = this.state;
    const isIe = detect().name === "ie";

    if (published) {
      return (
        <Wrapper isIe={isIe} data-test-id="applicant-review-page-body">
          {loading ? <Loading /> : <ReviewAlreadyLeft />}
        </Wrapper>
      );
    }

    return (
      <Wrapper isIe={isIe} data-test-id="applicant-review-page-body">
        {ieMessageActive && (
          <InternetExplorerSupport closeIeMessage={this.closeIeMessage} />
        )}
        {loading ? (
          <Loading />
        ) : (
          <FormWrapper>
            {error ? (
              <OopsPage error={error} />
            ) : (
              <Fragment>
                <Title
                  className="animated fadeInUp delay-2"
                  data-test-id="applicant-review-form-title"
                >
                  {step === 6
                    ? "Thank you!"
                    : `Leave ${recruiterName} a review!`}
                </Title>
                <Form onSubmit={this.handleSubmit}>
                  <FormStep
                    step={step}
                    termsAccepted={termsAccepted}
                    checkBoxOnChange={this.checkBoxOnChange}
                    candidateName={candidateName}
                    employerName={employerName}
                    recruiterName={recruiterName}
                    agencyName={agencyName}
                    jobTitle={jobTitle}
                    proceedToReview={this.proceedToReview}
                    setRating={this.setRating}
                    communicationRating={communicationRating}
                    professionalismRating={professionalismRating}
                    supportRating={supportRating}
                    recommendRating={recommendRating}
                    setReviewMessage={this.setReviewMessage}
                    reviewMessage={reviewMessage}
                    formLoading={formLoading}
                  />
                </Form>
              </Fragment>
            )}
          </FormWrapper>
        )}
      </Wrapper>
    );
  }
}

ApplicantReviewForm.propTypes = {
  match: PropTypes.shape({
    url: PropTypes.string,
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
};

export default ApplicantReviewForm;
